import { Alert, Space, Typography } from 'antd'
import { stripArrayRowIds } from 'features/libra/fieldValues/arrayRowIds'
import { formatRaw } from 'features/libra/llmDiff/formatFieldValue'
import {
  FieldValue,
  FieldValueSource,
  SourceType,
} from 'features/libra/ontology/ontologyFieldTypes'
import {
  DocumentSourcePopover,
  ReadOnlySourceDisplay,
} from 'features/libra/stepComponents/conditionalForm/conditionalFormSection/SourceInput'
import type { FC } from 'react'
import styled from 'styled-components'

import { AiCitationChip, extractLlmCitation } from './AiCitationChip'

export type AiPrefillDisagreement = {
  fieldId: string
  fieldLabel: string
  current: FieldValue
  suggestion: FieldValue
}

export const AiPrefillDisagreements: FC<{
  disagreements: ReadonlyArray<AiPrefillDisagreement>
  hasDocumentProvider: boolean
}> = ({ disagreements, hasDocumentProvider }) => {
  return (
    <Alert
      type='info'
      title='AI suggestions differ from retained values'
      description={
        <Space orientation='vertical' size='middle'>
          <Typography.Text>
            Edit a field to replace its current value.
          </Typography.Text>
          <Space orientation='vertical' size='middle'>
            {disagreements.map(
              ({ fieldId, fieldLabel, current, suggestion }) => (
                <Space key={fieldId} orientation='vertical' size='small'>
                  <Typography.Text strong>{fieldLabel}</Typography.Text>
                  <ValueColumns>
                    <Space orientation='vertical' size='small' align='start'>
                      <Typography.Text strong>
                        Current value kept
                      </Typography.Text>
                      <ValueDisplay>
                        {formatRaw(stripArrayRowIds(current.value))}
                      </ValueDisplay>
                      {renderCurrentSource(current.source, hasDocumentProvider)}
                    </Space>
                    <Space orientation='vertical' size='small' align='start'>
                      <Typography.Text strong>AI suggestion</Typography.Text>
                      <ValueDisplay>
                        {formatRaw(stripArrayRowIds(suggestion.value))}
                      </ValueDisplay>
                      {extractLlmCitation(suggestion) ? (
                        <Space size='small'>
                          <Typography.Text type='secondary'>
                            Source:
                          </Typography.Text>
                          <AiCitationChip fieldValue={suggestion} />
                        </Space>
                      ) : null}
                    </Space>
                  </ValueColumns>
                </Space>
              )
            )}
          </Space>
        </Space>
      }
    />
  )
}

function renderCurrentSource(
  source: FieldValueSource | undefined,
  hasDocumentProvider: boolean
) {
  if (!source) return null

  const display = <ReadOnlySourceDisplay source={source} />
  return source.type === SourceType.DOCUMENT && hasDocumentProvider ? (
    <DocumentSourcePopover source={source}>{display}</DocumentSourcePopover>
  ) : (
    display
  )
}

const ValueDisplay = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

const ValueColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 45rem) {
    grid-template-columns: 1fr;
  }
`
